import type { SigningProgressEvent, SigningProgressStage, SigningRequest } from "@vericreator/contracts";
import { signingExecutionStages as executionStages } from "@vericreator/contracts";
import { ArrowUpRight, Check, Circle, CircleAlert, Clock3, LoaderCircle, RotateCcw, Square } from "lucide-react";
import { useEffect, useState } from "react";
import type { SigningProgressTimeline } from "./useSigningProgress";

interface RequestListProps {
  readonly requests: readonly SigningRequest[];
  readonly isRetryingRequestId: string | null;
  readonly isCancellingRequestId: string | null;
  readonly isCancellingAll: boolean;
  readonly progressByRequestId: Readonly<Record<string, SigningProgressTimeline>>;
  onRetry(request: SigningRequest): void;
  onCancel(request: SigningRequest): void;
  onCancelAll(): void;
}

const statusContent = {
  resolving_source: { label: "Localizando fonte", Icon: LoaderCircle },
  awaiting_source_selection: { label: "Aguardando escolha", Icon: Clock3 },
  queued: { label: "Na fila", Icon: Clock3 },
  signing: { label: "Assinando", Icon: LoaderCircle },
  completed: { label: "Registro concluído", Icon: Check },
  failed: { label: "Requer atenção", Icon: CircleAlert }
} as const;

const phaseCopy = {
  queued: "Na fila segura",
  acquiring_source: "Coletando a fonte pública",
  signing_video: "Construindo a assinatura V3",
  storing_evidence: "Guardando provas do Core",
  finalizing_record: "Finalizando o registro",
  completed: "Registro concluído",
  failed: "Assinatura interrompida"
} as const;

export function RequestList({
  requests,
  isRetryingRequestId,
  isCancellingRequestId,
  isCancellingAll,
  progressByRequestId,
  onRetry,
  onCancel,
  onCancelAll
}: RequestListProps) {
  const activeRequests = requests.filter(isActiveRequest);
  const historyRequests = requests.filter((request) => !isActiveRequest(request));
  const hasCancellableRequests = activeRequests.some(isCancellable);
  return (
    <>
      {activeRequests.length > 0 && (
        <section className="active-signings" aria-labelledby="active-signings-heading">
          <div className="section-heading compact active-signings-heading">
            <div>
              <span className="eyebrow">Agora</span>
              <h2 id="active-signings-heading">{activeRequests.length === 1 ? "Assinatura em processamento" : "Assinaturas em processamento"}</h2>
            </div>
            {hasCancellableRequests && (
              <button
                className="request-cancel-all"
                disabled={isCancellingAll || isCancellingRequestId !== null}
                onClick={() => {
                  if (window.confirm("Interromper todos os pedidos ativos e apagar as evidências ainda não registradas? Esta ação não pode ser desfeita.")) {
                    onCancelAll();
                  }
                }}
                type="button"
              >
                {isCancellingAll ? <LoaderCircle className="spin" size={13} /> : <Square size={13} />}
                Interromper e limpar todos
              </button>
            )}
          </div>
          <div className="active-signings-stack">
            {activeRequests.map((request) => (
              <ActiveSigningCard
                isCancelling={isCancellingRequestId === request.id}
                isCancellingAll={isCancellingAll}
                key={request.id}
                onCancel={onCancel}
                request={request}
                timeline={progressByRequestId[request.id]}
              />
            ))}
          </div>
        </section>
      )}

      <section className="request-list" aria-labelledby="requests-heading">
        <div className="section-heading compact">
          <div>
            <span className="eyebrow">Histórico</span>
            <h2 id="requests-heading">Assinaturas recentes</h2>
          </div>
          <button className="text-button" type="button">Ver biblioteca <ArrowUpRight size={15} /></button>
        </div>
        <div className="requests-table" role="list">
          {historyRequests.map((request, index) => (
            <HistoryRequestRow
              isRetrying={isRetryingRequestId === request.id}
              key={request.id}
              onRetry={onRetry}
              request={request}
              striped={index % 2 === 1}
            />
          ))}
          {historyRequests.length === 0 && <p className="request-history-empty">As assinaturas concluídas ou interrompidas aparecerão aqui.</p>}
        </div>
      </section>
    </>
  );
}

const stageCopy: Record<SigningProgressStage, string> = {
  source_discovery: "Localizando a fonte pública",
  source_download: "Baixando a cópia pública",
  source_assembly: "Montando faixas sem recodificar",
  source_inspection: "Conferindo a mídia recebida",
  evidence_preparation: "Preparando a evidência",
  source_staging: "Distribuindo a fonte temporária",
  reducer_inputs: "Coletando insumos para finalizar",
  luma_evidence: "Calculando a trilha visual contínua",
  v3_atlas_anchors: "Construindo âncoras V3 e Atlas",
  audio_avlink: "Vinculando áudio e vídeo",
  sealing_segments: "Selando segmentos assinados",
  uploading_evidence: "Guardando provas do Core",
  finalizing_record: "Finalizando o registro",
  queued: "Na fila segura",
  completed: "Registro concluído",
  failed: "Assinatura interrompida"
};

const stageProgressFloor: Record<typeof executionStages[number], number> = {
  source_discovery: 15, source_download: 20, source_inspection: 31,
  source_assembly: 39, evidence_preparation: 46, source_staging: 52,
  luma_evidence: 58, reducer_inputs: 70, v3_atlas_anchors: 75,
  audio_avlink: 84, sealing_segments: 88, uploading_evidence: 92, finalizing_record: 96
};

function ActiveSigningCard({
  request,
  timeline,
  isCancelling,
  isCancellingAll,
  onCancel
}: {
  readonly request: SigningRequest;
  readonly timeline: SigningProgressTimeline | undefined;
  readonly isCancelling: boolean;
  readonly isCancellingAll: boolean;
  readonly onCancel: (request: SigningRequest) => void;
}) {
  // D1 checkpoints are deliberately sparse. While a live progress stream is
  // present, it is the authoritative indication that the Container is
  // executing, even if the durable queue checkpoint still reads "queued".
  const isExecuting = request.status === "signing" || timeline !== undefined;
  const status = isExecuting ? statusContent.signing : statusContent[request.status];
  const StatusIcon = status.Icon;
  return (
    <article className="active-signing-card">
      <div className="active-signing-topline">
        <div className="request-source">
          <span className={`provider-orb ${request.provider ?? "file"}`}>{sourceInitial(request)}</span>
          <div>
            <strong>{request.title}</strong>
            <span>
              {request.provider ?? "Arquivo"} · iniciado {formatDate(request.updatedAt)}
              {timeline && timeline.attempt > 1 ? ` · tentativa ${timeline.attempt}` : ""}
            </span>
          </div>
        </div>
        <div className={`request-status ${isExecuting ? "signing" : request.status}`}>
          <StatusIcon className={isExecuting ? "spin" : undefined} size={15} />
          {status.label}
        </div>
        <button
          className="request-cancel"
          disabled={isCancellingAll || isCancelling}
          onClick={() => {
            if (window.confirm(`Interromper e limpar “${request.title}”? A assinatura não será concluída.`)) {
              onCancel(request);
            }
          }}
          type="button"
        >
          {isCancelling ? <LoaderCircle className="spin" size={13} /> : <Square size={13} />}
          {request.failureCode === "cancelled_by_owner" ? "Concluir limpeza" : "Abortar e limpar"}
        </button>
      </div>
      <ProgressBar request={request} timeline={timeline} />
      <ExecutionTiming request={request} timeline={timeline} />
      <ExecutionStageRoadmap request={request} timeline={timeline} />
    </article>
  );
}

function HistoryRequestRow({
  request,
  striped,
  isRetrying,
  onRetry
}: {
  readonly request: SigningRequest;
  readonly striped: boolean;
  readonly isRetrying: boolean;
  readonly onRetry: (request: SigningRequest) => void;
}) {
  const status = statusContent[request.status];
  const StatusIcon = status.Icon;
  return (
    <article className={`request-row history-row ${striped ? "zebra" : ""}`} role="listitem">
      <div className="request-source">
        <span className={`provider-orb ${request.provider ?? "file"}`}>{sourceInitial(request)}</span>
        <div>
          <strong>{request.title}</strong>
          <span>{request.provider ?? "Arquivo"} · {formatDate(request.updatedAt)}</span>
        </div>
      </div>
      <div className={`request-status ${request.status}`}>
        <StatusIcon size={15} />
        {status.label}
      </div>
      <div className={`request-progress ${request.progress.phase}`}>
        <ProgressBar request={request} timeline={undefined} />
        {request.status === "failed" && (
          <span className="request-failure-copy">{failureCopy(request.failureCode)}</span>
        )}
        {request.status === "failed" && (
          <button className="request-retry" disabled={isRetrying} onClick={() => onRetry(request)} type="button">
            {isRetrying ? <LoaderCircle className="spin" size={13} /> : <RotateCcw size={13} />}
            Tentar novamente
          </button>
        )}
      </div>
      <div className="request-proof">
        <span className="request-protocol">{protocolLabel(request)}</span>
        {request.record?.verificationState === "published" && request.record.publicVerificationUrl && (
          <a href={request.record.publicVerificationUrl} rel="noreferrer" target="_blank">
            Ver registro <ArrowUpRight size={13} />
          </a>
        )}
        {request.record?.verificationState === "shadow" && <span>Registro técnico em validação</span>}
      </div>
    </article>
  );
}

function ProgressBar({ request, timeline }: { readonly request: SigningRequest; readonly timeline: SigningProgressTimeline | undefined }) {
  const progress = progressPresentation(request, timeline);
  return (
    <>
      <div className="request-progress-copy">
        <span>{progress.label}</span>
        <strong>{progress.percent}%</strong>
      </div>
      <div
        aria-label={`${progress.label}: ${progress.percent}%`}
        aria-valuemax={100}
        aria-valuemin={0}
        aria-valuenow={progress.percent}
        className="request-progress-track"
        role="progressbar"
      >
        <span style={{ width: `${progress.percent}%` }} />
      </div>
    </>
  );
}

function ExecutionStageRoadmap({ request, timeline }: { readonly request: SigningRequest; readonly timeline: SigningProgressTimeline | undefined }) {
  return (
    <div aria-live="polite" className="execution-stage-grid">
      {executionStages.map((stage) => {
        const event = timeline?.byStage[stage];
        const state = executionStageState(request, timeline, stage, event);
        return (
        <div className={`execution-stage ${state}`} key={stage}>
          <span className="request-progress-step-indicator">
            {state === "complete" ? <Check size={11} /> : state === "active" ? <LoaderCircle className="spin" size={11} /> : <Circle size={10} />}
          </span>
          <span>{stageCopy[stage]}</span>
          <strong>{event ? formatProgressMeasurement(event) : state === "active" ? "em andamento" : state === "complete" ? "concluído" : "aguardando"}</strong>
        </div>
        );
      })}
    </div>
  );
}

function executionStageState(
  request: SigningRequest,
  timeline: SigningProgressTimeline | undefined,
  stage: typeof executionStages[number],
  event: SigningProgressTimeline["latest"] | undefined
): "complete" | "active" | "pending" {
  if (event?.state === "completed") return "complete";
  // Continuous Luma and the audio/AVLink pass may overlap by design. When a
  // runner reports such parallel work, keep its visual marker consistent with
  // the measurement instead of displaying "em andamento" beside a pending
  // circle. Earlier stages without an explicit running event still resolve
  // from the monotonic sequence below.
  if (event?.state === "running") return "active";
  const stageIndex = executionStages.indexOf(stage);
  const latest = timeline?.latest;
  const observedIndex = latest ? executionStages.indexOf(latest.stage as typeof executionStages[number]) : -1;
  if (observedIndex >= 0) {
    if (stageIndex < observedIndex) return "complete";
    if (stageIndex === observedIndex && latest?.state === "running") return "active";
    return "pending";
  }
  const inferredIndex = inferredStageIndex(request);
  if (inferredIndex === undefined) return "pending";
  if (stageIndex < inferredIndex) return "complete";
  if (stageIndex === inferredIndex) return "active";
  return "pending";
}

function inferredStageIndex(request: SigningRequest): number | undefined {
  const inferredStage = request.progress.phase === "acquiring_source"
    ? "source_discovery"
    : request.progress.phase === "signing_video"
      ? "evidence_preparation"
      : request.progress.phase === "storing_evidence"
        ? "uploading_evidence"
        : request.progress.phase === "finalizing_record"
          ? "finalizing_record"
          : undefined;
  if (!inferredStage) return undefined;
  const index = executionStages.indexOf(inferredStage);
  return index >= 0 ? index : undefined;
}

interface ProgressPresentation {
  readonly label: string;
  readonly percent: number;
}

function progressPresentation(request: SigningRequest, timeline: SigningProgressTimeline | undefined): ProgressPresentation {
  const latest = timeline?.latest;
  if (!latest || request.status === "failed" || request.status === "completed") {
    return { label: phaseCopy[request.progress.phase], percent: request.progress.percent };
  }
  const index = executionStages.indexOf(latest.stage as typeof executionStages[number]);
  if (index < 0 || index >= executionStages.length) {
    return { label: phaseCopy[request.progress.phase], percent: request.progress.percent };
  }
  // Stages have deliberately conservative ranges. When the runner knows a
  // reliable total (bytes, segments or artifacts), use it only inside that
  // stage's range. The durable checkpoint remains a floor, so delayed source
  // observations can never pull the overall bar backward.
  const floor = stageProgressFloor[executionStages[index]!];
  const nextStage = executionStages[index + 1];
  const next = nextStage ? stageProgressFloor[nextStage] : 98;
  const measuredRatio = latest.current !== undefined && latest.total !== undefined && latest.total > 0
    ? Math.min(0.96, latest.current / latest.total)
    : 0;
  const measured = Math.round(floor + ((next - floor) * measuredRatio));
  const percent = Math.max(request.progress.percent, floor, Math.min(98, measured));
  const stage = executionStages[Math.min(index, executionStages.length - 1)]!;
  return { label: stageCopy[stage], percent };
}

function ExecutionTiming({ request, timeline }: { readonly request: SigningRequest; readonly timeline: SigningProgressTimeline | undefined }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const interval = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(interval);
  }, []);

  const startedAt = Date.parse(request.createdAt);
  const elapsedMilliseconds = Number.isFinite(startedAt) ? Math.max(0, now - startedAt) : 0;
  const progress = progressPresentation(request, timeline);
  const estimatedTotalMilliseconds = estimateTotalDuration(elapsedMilliseconds, progress.percent);
  const preparation = timeline?.byStage.evidence_preparation?.preparationTiming;
  return (
    <div className="execution-timing" aria-live="polite">
      <span><Clock3 size={12} /> Tempo total <strong>{formatDuration(elapsedMilliseconds)}</strong></span>
      {preparation && (
        <span title={`Índice/contagem: ${formatDuration(preparation.timelineMs)}; SHA-256: ${formatDuration(preparation.digestMs)}; áudio: ${formatDuration(preparation.audioMs)}; selagem privada: ${formatDuration(preparation.sealMs)}. Operações paralelas, não somar durações.`}>
          Preparação <strong>{formatDuration(preparation.totalMs)}</strong>
        </span>
      )}
      <span>
        Previsão dinâmica
        <strong>{estimatedTotalMilliseconds === undefined ? "calculando" : `≈ ${formatDuration(estimatedTotalMilliseconds)} no total`}</strong>
      </span>
    </div>
  );
}

function estimateTotalDuration(elapsedMilliseconds: number, percent: number): number | undefined {
  // The first seconds and the final sealing stage do not offer enough signal
  // for an honest projection. Avoid false precision; once work is underway,
  // present only a broadly rounded estimate that improves as it advances.
  if (elapsedMilliseconds < 15_000 || percent < 12 || percent >= 97) {
    return undefined;
  }
  return elapsedMilliseconds * (100 / percent);
}

function formatDuration(milliseconds: number): string {
  const seconds = Math.max(0, Math.round(milliseconds / 1_000));
  const hours = Math.floor(seconds / 3_600);
  const minutes = Math.floor((seconds % 3_600) / 60);
  const remainingSeconds = seconds % 60;
  if (hours > 0) return `${hours}h ${minutes.toString().padStart(2, "0")}min`;
  if (minutes > 0) return `${minutes}min ${remainingSeconds.toString().padStart(2, "0")}s`;
  return `${remainingSeconds}s`;
}

function formatProgressMeasurement(event: SigningProgressEvent): string {
  if (event.current === undefined || !event.unit) {
    return event.state === "completed" ? "concluído" : "em andamento";
  }
  const current = event.unit === "bytes" ? formatBytes(event.current) : event.current.toLocaleString("pt-BR");
  const total = event.total === undefined
    ? undefined
    : event.unit === "bytes"
      ? formatBytes(event.total)
      : event.total.toLocaleString("pt-BR");
  const label = event.unit === "bytes" ? "transferidos" : event.unit === "frames" ? "frames" : event.unit === "anchors" ? "âncoras" : event.unit === "segments" ? "segmentos" : "artefatos";
  return total ? `${current} de ${total} ${label === "transferidos" ? "" : label}`.trim() : `${current} ${label}`;
}

function formatBytes(value: number): string {
  if (value < 1024 * 1024) return `${Math.max(1, Math.round(value / 1024)).toLocaleString("pt-BR")} KB`;
  return `${(value / (1024 * 1024)).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} MB`;
}

function protocolLabel(request: SigningRequest): string {
  return (request.record?.evidenceProfile ?? request.evidenceProfile) === "vc-v3-longform-luma-evidence-v2"
    ? "V3 Compact Evidence · shadow"
    : request.protocol;
}

function isCancellable(request: SigningRequest): boolean {
  return request.status === "resolving_source"
    || request.status === "awaiting_source_selection"
    || request.status === "queued"
    || request.status === "signing"
    || (request.status === "failed" && request.failureCode === "cancelled_by_owner");
}

function isActiveRequest(request: SigningRequest): boolean {
  return isCancellable(request);
}

function sourceInitial(request: SigningRequest): string {
  if (request.provider === "youtube") return "Y";
  if (request.provider === "instagram") return "I";
  if (request.provider === "tiktok") return "T";
  return "F";
}

function formatDate(value: string): string {
  return new Intl.DateTimeFormat("pt-BR", { dateStyle: "medium" }).format(new Date(value));
}

function failureCopy(code: SigningRequest["failureCode"]): string {
  switch (code) {
    case "core_resource_exhausted":
      return "A fonte excedeu a capacidade temporária de processamento do piloto; nenhum vídeo foi retido.";
    case "core_media_unsupported":
      return "A representação recebida não é compatível com o perfil atual de assinatura.";
    case "core_runtime_unavailable":
      return "O ambiente de assinatura não ficou disponível. Tente novamente mais tarde.";
    case "core_artifact_generation_failed":
      return "A assinatura foi interrompida ao gerar a evidência local necessária para validação de crop.";
    case "distributed_preparation_failed":
      return "A preparação segura da assinatura distribuída não ficou disponível. Nenhum vídeo foi retido.";
    case "distributed_source_transfer_failed":
      return "A fonte foi obtida, mas não pôde ser preparada para o processamento distribuído. Nenhum vídeo foi retido.";
    case "distributed_source_activation_failed":
      return "A fonte foi preparada, mas os blocos distribuídos não puderam ser ativados. Nenhum vídeo foi retido.";
    case "artifact_upload_failed":
      return "A assinatura foi concluída localmente, mas as evidências não puderam ser guardadas.";
    case "source_media_unavailable":
    case "source_acquisition_failed":
      return "A plataforma de origem não disponibilizou uma cópia utilizável neste momento.";
    default:
      return "O processo foi interrompido de forma segura; tente novamente após revisar a fonte.";
  }
}
